import moment from "moment";
import { cloneDeep } from "lodash";

import { timeUnitMapping } from "service/proqueryService";
import { ConsumptionChartBy } from "types";
import {
    summaryReportChartSeries,
    getAverageFromSeriesAndTotal,
} from "./report";
import {
    transformConsumptionDataToOverallData,
    transformConsumptionDataToIndividualData,
} from './reportTransform';
import { initialReportItemsStateData } from "./reportInitData";
import { convertSelectedIdsToDevices, convertSelectedIdsToRooms } from "./reportConvert";
import {
    ReportChartSeries,
    NullableNumber,
    ReportItemsState,
    ReportItemStateData,
    ReportSensorMeterType,
} from "types/Reports";
import { getWeekdaysWeekendsIndexInMonth } from "utils/datetime";
import { REPORT_METER_SENSOR_LIST } from "constant/report";


export const getRandomFloat = (min: number, max: number) => {
    const array = new Uint32Array(1);
    globalThis.crypto.getRandomValues(array);

    // Convert to floating point number in [0, 1)
    const random = array[0] / (0xFFFFFFFF + 1);
    return random * (max - min) + min;
}

export const getCategoryOfMonth = (day: moment.Moment) => {
    const daysInMonth = day.daysInMonth();
    const categories = Array.from({ length: daysInMonth }, (_v, i) => `${i + 1}`);
    return categories;
}

export const generateRoomChartSeries = (
    numberOfRooms: number,
    numberOfDays: number,
    lastTime: moment.Moment,
) => {
    const { weekdayIndexes, weekendIndexes } = getWeekdaysWeekendsIndexInMonth(lastTime);
    const minDay = 0;
    const maxDay = 24;

    const categories: string[] = Array.from({ length: numberOfDays }, (_, index) => `${index + 1}`);

    const series: ReportChartSeries[] = Array.from({ length: numberOfRooms }, (_, index) => {
        const name = `Room ${index + 1}`;
        const id = `room_id_${index + 1}`;

        const data: NullableNumber[] = Array.from({ length: numberOfDays }, (_, __) => getRandomFloat(minDay, maxDay));
        const weekdaysData: NullableNumber[] = data.map((value, index) => {
            if (weekdayIndexes.includes(index)) {
                return value;
            }

            return null;
        });
        const weekendsData: NullableNumber[] = data.map((value, index) => {
            if (weekendIndexes.includes(index)) {
                return value;
            }

            return null;
        });

        return { id, name, data, weekdaysData, weekendsData };
    });

    return { series, categories };
};

export const generateReportItemDataOfRoom = (
    numberOfRooms: number,
    endDayOfMonth: moment.Moment,
) => {
    const numberOfDays = endDayOfMonth.daysInMonth();
    const groupBy = ConsumptionChartBy.Month;
    const timeUnit = timeUnitMapping[groupBy];
    const previousTime = moment(endDayOfMonth).subtract(1, timeUnit);

    const curSeries = generateRoomChartSeries(numberOfRooms, numberOfDays, endDayOfMonth);
    const prevSeries = generateRoomChartSeries(numberOfRooms, previousTime.daysInMonth(), previousTime);

    const roomSummaryInfo = summaryReportChartSeries(
        numberOfRooms === 0,
        numberOfRooms === 0,
        curSeries.series,
        prevSeries.series,
    );

    const prevAverage = getAverageFromSeriesAndTotal(
        roomSummaryInfo.all.previousTotal,
        roomSummaryInfo.weekdays.previousTotal,
        roomSummaryInfo.weekends.previousTotal,
        prevSeries.series,
    );

    const roomsData = transformConsumptionDataToOverallData(
        roomSummaryInfo.all.total,
        roomSummaryInfo.weekdays.total,
        roomSummaryInfo.weekends.total,
        curSeries.series,
        curSeries.categories,
        endDayOfMonth,
        prevAverage,
    );

    // convert from consumption data to individualMetersData
    const individualRoomsData = transformConsumptionDataToIndividualData(
        curSeries.series,
        curSeries.categories,
        endDayOfMonth,
    );

    return { roomSummaryInfo, roomsData, individualRoomsData, curSeries, prevSeries };
};

export const generateItemEmptySeries = (
    id: string,
    name: string,
): ReportChartSeries => {
    return {
        id,
        name,
        data: [],
        weekdaysData: [],
        weekendsData: [],
    };
};

export const generateReportPageEmptyData = (
    items: ReportItemsState,
    isApplied: boolean,
) => {
    const usedIds = isApplied ? 'appliedIds' : 'selectedIds';
    const emptyItemsData: ReportItemStateData = cloneDeep(initialReportItemsStateData);

    const usedRooms = convertSelectedIdsToRooms(items.room.all, items.room[usedIds]);

    for (const room of usedRooms) {
        const emptySeries = generateItemEmptySeries(room.roomUUId, room.roomName);
        emptyItemsData.room.chartSeries.push(cloneDeep(emptySeries));
        emptyItemsData.room.prevChartSeries.push(cloneDeep(emptySeries));
    }

    for (const itemType of REPORT_METER_SENSOR_LIST) {
        const type = itemType as ReportSensorMeterType;

        if (items[type].all.length === 0) {
            continue;
        }

        const usedItems = convertSelectedIdsToDevices(items[type].all, items[type][usedIds]);

        for (const item of usedItems) {
            const id = `${item.gatewayId}-${item.deviceId}`;

            const emptySeries = generateItemEmptySeries(id, item.sensorName);

            emptyItemsData[type].chartSeries.push(cloneDeep(emptySeries));
            emptyItemsData[type].prevChartSeries.push(cloneDeep(emptySeries));
        }
    }

    return emptyItemsData;
};
